import { useState, useCallback, useMemo } from 'react';
import { useLocale } from '@repo/i18n';
import { useConfigurations } from '@repo/widget-utils/widgetsConfiguration';
import {
    getDefaultGuestTypes,
    updateSimpleQuantity,
    getTotalGuests,
} from '@repo/widget-utils/booking/domains/simple/simpleQuantities';
import { applyPersonsMaxLimit } from '@repo/widget-utils/booking/domains/shared/capacity-calculators';
import type { QuantityInputItem } from '@repo/widget-utils/booking/domains/shared/types';

export type UseSimpleQuantitiesResult = {
    /** UI-ready input items (adults and children counters) */
    items: QuantityInputItem[];

    /** Handler for quantity changes */
    onChange: (id: string, newQuantity: number) => void;

    /** Total number of guests (sum of all quantities) */
    totalGuests: number;

    /** Reset quantities to defaults (adults=1, children=0) */
    reset: () => void;
};

/**
 * Hook for simple quantity counters (leads forms)
 *
 * Manages local state for adults/children counters.
 * No booking context integration - purely for form submission.
 *
 * @returns Object with items, onChange, and totalGuests
 *
 * @example
 * function LeadsForm() {
 *   const { items, onChange, totalGuests } = useSimpleQuantities();
 *
 *   return (
 *     <div>
 *       <MultipleNumberInput items={items} onChange={onChange} />
 *       <p>Total guests: {totalGuests}</p>
 *     </div>
 *   );
 * }
 */
export function useSimpleQuantities(): UseSimpleQuantitiesResult {
    const { t } = useLocale();
    const config = useConfigurations();

    const [rawItems, setRawItems] = useState<QuantityInputItem[]>(() => getDefaultGuestTypes(t));

    const items = useMemo(() => {
        if (config.personsMax && config.personsMax > 0) {
            return applyPersonsMaxLimit(rawItems, config.personsMax);
        }
        return rawItems;
    }, [rawItems, config.personsMax]);

    const onChange = useCallback((id: string, newQuantity: number) => {
        setRawItems((prevItems) => updateSimpleQuantity(prevItems, id, newQuantity));
    }, []);

    const totalGuests = useMemo(() => getTotalGuests(rawItems), [rawItems]);

    const reset = useCallback(() => {
        setRawItems(getDefaultGuestTypes(t));
    }, [t]);

    return {
        items,
        onChange,
        totalGuests,
        reset,
    };
}
